/* eslint-disable no-console */
import {CrownFilled, InfoCircleOutlined} from '@ant-design/icons';
import {Col, Flex, Form, Input, Row, Select, Switch} from 'antd';
import useFormInstance from 'antd/es/form/hooks/useFormInstance';
import moment from 'moment';
import React, {useEffect, useState} from 'react';
import {useSearchParams} from 'react-router-dom';

import {ReactComponent as YearCalender} from '../../assets/yearCalender.svg';
import {CommonErrors, transformInput} from '../../constants/Common';
import FilteredUnit from '../../constants/FilteredUnit';
import InformativeText from '../../constants/InformativeText';
import {
  // OPKey,
  OPplaceholder
} from '../../pages/configurator/orgFacility/orgInfo/constants/orgProfileKeyLabel';
import facilityHQFetch from '../../services/modules/OM/facilityHQDetails';
import NumberInput from '../../shared/NumberInput';
// import useCompanyStore from '../../store/companyStore/companyDetailsStore';
// import {hasSubCategory} from '../../utils/companyCategorySubCategory';
import useJSONStore from '../../store/StaticJson/jsonStore';
import {
  AllCap,
  FirstCap,
  eachWordCap,
  eachWordCapOrAllCap
} from '../../utils/FormUtils/FirstCap';
import RegEx from '../../utils/FormUtils/RegEx';
import {yearSelect} from '../../utils/FormUtils/yearSelect';
import {shouldBlockFreeOrReadOnly} from '../../utils/SubscriptionHelper';
import PhoneNumberInput from '../CustomInputs/PhoneNumberInput/PhoneNumberInput';
import SelectWithOtherOption, {
  selectWithOtherBusinessNameRule
} from '../CustomInputs/Select/SelectWithOtherOption';
import MomentDatePicker from '../DatePicker/MomentDatePicker';
import {facilityTypeIds, yesNo} from '../Table/Constants/Common';

import {getDatePickerToMomentProps} from './Constants/commonConstants';
import {yearKeyLabel} from './Constants/IP/commonKeyLabel';
import {FacilityKey, FacilityLabel} from './Constants/IP/FacilityKeyLabel';
import {filterSelectOptions} from './Utils/commonFunctions';

const FacilityForm: React.FC<{
  data?: any;
  currentStep?: number;
  setSelectedCountry: React.Dispatch<React.SetStateAction<string>>;
  isHeadquarterExist: boolean;
}> = ({
  data = undefined,
  currentStep,
  setSelectedCountry,
  isHeadquarterExist = false
}) => {
  const countryOptions = useJSONStore((state) => state.country);
  const stateOptions = useJSONStore((state) => state.states);
  const cityOptions = useJSONStore((state) => state.city);
  const facilityType = useJSONStore((state) => state.facilityType);
  // const rating = useJSONStore.getState().rating;
  // const resType = useJSONStore.getState().resType;
  // const companyDetails = useCompanyStore.getState().companyDetails;

  const FormInstance = useFormInstance();
  const {setFields, getFieldValue} = FormInstance;

  const [isLeased, setIsLeased] = useState(
    data?.[FacilityKey.LEASED] ? data?.[FacilityKey.LEASED] : undefined
  );

  const [addToPastYears, setAddToPastYears] = useState(false);
  const [allFacilityHeadquarter, setAllFacilityHeadquarter] = useState<any[]>(
    []
  );
  const [pastYearChange, setPastYearChange] = useState(false);
  const [duplicateFacilityMessage, setDuplicateFacilityMessage] = useState('');

  const faciInfo = InformativeText?.FacilityInfo;
  const yearFilter = yearSelect();
  const [searchParam] = useSearchParams();
  const [updatedFacilityType, setUpdatedFacilityType] = useState<any[]>([]);
  const [selectedYear, setSelectedYear] = useState<string>(
    searchParam.get('selectedYear') ?? ''
  );

  const resetAddPastYears = () => {
    if (addToPastYears) {
      // Whenever selectedYear changes, reset the "Add to past years" switch to No
      FormInstance.setFieldsValue({
        [FacilityKey.ADD_TO_PAST_YEARS]: false,
        [FacilityKey.PAST_YEAR_FROM]: undefined,
        [FacilityKey.PAST_YEAR_TO]: undefined
      });

      // Also clear any validation errors on these fields
      FormInstance.setFields([
        {name: FacilityKey.ADD_TO_PAST_YEARS, errors: []},
        {name: FacilityKey.PAST_YEAR_FROM, errors: []},
        {name: FacilityKey.PAST_YEAR_TO, errors: []}
      ]);

      // Update local state
      setAddToPastYears(false);
      setDuplicateFacilityMessage('');
    }
  };

  useEffect(() => {
    if (
      isHeadquarterExist &&
      (data === undefined || data?.type !== facilityTypeIds.headquarter) &&
      searchParam.get('selectedYear') === selectedYear
    ) {
      const filteredData: any[] = facilityType?.filter((item: any) => {
        return item?.value !== facilityTypeIds.headquarter;
      });
      setUpdatedFacilityType(filteredData);
    } else {
      setUpdatedFacilityType(facilityType);
    }
    resetAddPastYears();
  }, [selectedYear]);

  useEffect(() => {
    if (
      addToPastYears &&
      FormInstance.getFieldValue(FacilityKey.FACILITY_TYPE) ===
        facilityTypeIds.headquarter
    ) {
      Promise.all([facilityHQFetch()])
        .then(([res1]) => {
          setAllFacilityHeadquarter(res1);
        })
        .catch(() => {});
    } else {
      setAllFacilityHeadquarter([]);
    }
  }, [addToPastYears]);

  useEffect(() => {
    const startYear = getFieldValue(FacilityKey.PAST_YEAR_FROM);
    const endYear = getFieldValue(FacilityKey.PAST_YEAR_TO);
    const type = FormInstance.getFieldValue(FacilityKey.FACILITY_TYPE);

    if (
      !startYear ||
      !endYear ||
      type !== facilityTypeIds.headquarter ||
      !allFacilityHeadquarter?.length
    ) {
      setDuplicateFacilityMessage('');

      return;
    }

    const getBaseYear = (value: string): number => {
      const base = value?.split('-')[0];
      const num = Number(base);

      return isNaN(num) ? 0 : num;
    };

    const start = getBaseYear(startYear);
    const end = getBaseYear(endYear);

    if (start === 0 || end === 0 || start > end) {
      setDuplicateFacilityMessage('');

      return;
    }

    // Find all overlapping HQ years in the selected range
    const overlappingYears = allFacilityHeadquarter
      .map((hq) => hq.year)
      .filter((year) => {
        const hqStart = getBaseYear(year);

        return hqStart >= start && hqStart <= end;
      });

    if (overlappingYears.length > 0) {
      setDuplicateFacilityMessage(
        `A financial year can have only one headquarter.
         Therefore, adding these facility as a headquarters is not allowed for the following years:
          ${overlappingYears.join(', ')}`
      );
    } else {
      setDuplicateFacilityMessage('');
    }
  }, [pastYearChange, allFacilityHeadquarter]);

  return (
    <>
      {currentStep === 0 && (
        <>
          <Col className="gutter-row">
            <Form.Item
              name={yearKeyLabel.key}
              label={yearKeyLabel.label}
              labelCol={{span: 24}}
              rules={[
                {
                  required: true,
                  message: CommonErrors.mandatory(yearKeyLabel.label)
                }
              ]}
              initialValue={searchParam.get('selectedYear')}
            >
              <Select
                placeholder={yearKeyLabel.label}
                options={yearFilter}
                onChange={(value) => {
                  setSelectedYear(value);
                }}
                suffixIcon={<YearCalender />}
                disabled={!!data?._id}
              />
            </Form.Item>
          </Col>
          <Col className="gutter-row">
            <Form.Item
              name={FacilityKey.FACILITY_NAME}
              rules={[
                {
                  required: true,
                  message: CommonErrors.mandatory(FacilityLabel.FACILITY_NAME)
                },
                {
                  max: 100,
                  message: `${FacilityLabel.FACILITY_NAME} should be less than 100 characters`
                },
                {
                  min: 3,
                  message: `${FacilityLabel.FACILITY_NAME} should be more than 2 characters`
                },
                {
                  pattern: RegEx.businessName,
                  message: CommonErrors.noAllowedSpecChar
                }
              ]}
              label={FacilityLabel.FACILITY_NAME}
              labelCol={{span: 24}}
              initialValue={''}
              normalize={(value) => transformInput.removeExtraSpace(value)}
              validateFirst
              hasFeedback
            >
              <Input
                className="input"
                placeholder={`Enter ${FacilityLabel.FACILITY_NAME}`}
                onInput={(e) => {
                  const target = e.target as HTMLInputElement;
                  target.value = eachWordCapOrAllCap(target.value);
                }}
              />
            </Form.Item>
          </Col>
          <Col className="gutter-row">
            <Form.Item
              name={FacilityKey.FACILITY_TYPE}
              label={FacilityLabel.FACILITY_TYPE}
              labelCol={{span: 24}}
              initialValue={undefined}
              rules={[selectWithOtherBusinessNameRule()]}
              validateFirst
              hasFeedback
            >
              <SelectWithOtherOption
                className="select"
                placeholder={`Choose ${FacilityLabel.FACILITY_TYPE}`}
                allowClear
                options={updatedFacilityType}
                showSearch
                filterOption={filterSelectOptions}
                disabled={data?.type === facilityTypeIds.headquarter}
                onChange={resetAddPastYears}
              />
            </Form.Item>
          </Col>
          <Col className="gutter-row">
            <Form.Item
              name={FacilityKey.LEASED}
              label={FacilityLabel.LEASED}
              labelCol={{span: 24}}
              rules={[
                {
                  required: true,
                  message: CommonErrors.mandatory(FacilityLabel.LEASED)
                }
              ]}
              tooltip={{
                icon: <InfoCircleOutlined />,
                title: faciInfo?.leased
              }}
              initialValue={isLeased}
              validateFirst
              hasFeedback
            >
              <Select
                className="select"
                placeholder={`Choose ${FacilityLabel.LEASED}`}
                options={yesNo.map((option) => {
                  return {
                    ...option,
                    label: (
                      <Flex align="center" justify="space-between">
                        <div>{option.label}</div>
                        {shouldBlockFreeOrReadOnly() &&
                          option.label === 'Yes' && (
                            <CrownFilled
                              style={{
                                color: '#FFD700',
                                paddingRight: 10,
                                paddingLeft: 10
                              }}
                              title="Upgrade Plan"
                            />
                          )}
                      </Flex>
                    ),
                    disabled:
                      shouldBlockFreeOrReadOnly() && option.label === 'Yes'
                  };
                })}
                onChange={(e) => {
                  setIsLeased(e);
                }}
              />
            </Form.Item>
          </Col>

          <Col className="gutter-row">
            <Form.Item dependencies={[FacilityKey.LEASED]} noStyle>
              {({getFieldValue}) => {
                return (
                  getFieldValue(FacilityKey.LEASED) && (
                    <>
                      <Form.Item
                        name={FacilityKey.LESSOR_NAME}
                        rules={[
                          // {
                          //   required: true,
                          //   message: CommonErrors.mandatory(
                          //     FacilityLabel.LESSOR_NAME
                          //   )
                          // },
                          {
                            max: 100,
                            message: `${FacilityLabel.LESSOR_NAME} should be less than 100 characters`
                          },
                          {
                            min: 3,
                            message: `${FacilityLabel.LESSOR_NAME} should be more than 2 characters`
                          },
                          {
                            pattern: RegEx.fullName,
                            message: CommonErrors.noSpecialChar
                          },
                          {
                            whitespace: true,
                            message: CommonErrors.mandatory(
                              FacilityLabel.LESSOR_NAME
                            )
                          }
                        ]}
                        label={FacilityLabel.LESSOR_NAME}
                        labelCol={{span: 24}}
                        initialValue={''}
                        tooltip={{
                          icon: <InfoCircleOutlined />,
                          title: faciInfo?.asset_leased_name
                        }}
                        validateFirst
                        hasFeedback
                      >
                        <Input
                          className="input"
                          placeholder={`Enter ${FacilityLabel.LESSOR_NAME}`}
                          onInput={(e) => {
                            const target = e.target as HTMLInputElement;
                            target.value = eachWordCap(target.value);
                          }}
                        />
                      </Form.Item>
                      <Form.Item
                        name={FacilityKey.MOBILE_NO}
                        rules={[
                          // {
                          //   required: true,
                          //   message: CommonErrors.mandatory(
                          //     FacilityLabel.MOBILE_NO
                          //   )
                          // },
                          {
                            // eslint-disable-next-line @typescript-eslint/promise-function-async
                            validator(_, value) {
                              const digitsOnly = (value ?? '')
                                .toString()
                                .replace(/\D/g, '');
                              if (!value || digitsOnly.length <= 3) {
                                return Promise.resolve();
                              }
                              if (RegEx.mobile.test(value)) {
                                return Promise.resolve();
                              } else {
                                return Promise.reject(
                                  new Error(
                                    CommonErrors.validField(
                                      FacilityLabel.MOBILE_NO
                                    )
                                  )
                                );
                              }
                            }
                          }
                        ]}
                        label={FacilityLabel.MOBILE_NO}
                        labelCol={{span: 24}}
                        initialValue={''}
                        validateFirst
                        hasFeedback
                      >
                        <PhoneNumberInput />
                      </Form.Item>
                      <Form.Item
                        name={FacilityKey.RENTED_FROM}
                        label={FacilityLabel.RENTED_FROM}
                        // getValueProps={() => ({
                        //   value: data?.[FacilityKey.RENTED_FROM]
                        //     ? moment(data?.[FacilityKey.RENTED_FROM])
                        //     : undefined
                        // })}
                        rules={[
                          {
                            required: true,
                            message: CommonErrors.mandatory(
                              FacilityLabel.RENTED_FROM
                            )
                          }
                        ]}
                        validateFirst={true}
                        hasFeedback
                        labelCol={{span: 24}}
                        {...getDatePickerToMomentProps()}
                      >
                        <MomentDatePicker
                          disabledDate={(current: any) => {
                            return (
                              moment() <= current || moment('2000/04') > current
                            );
                          }}
                          inputReadOnly
                          format="D MMM YYYY"
                          style={{width: '100%'}}
                          showNow={false}
                          onChange={() => {
                            if (getFieldValue(FacilityKey.RENTED_TO)) {
                              setFields([
                                {
                                  name: FacilityKey.RENTED_TO,
                                  errors: [CommonErrors.selectAgain],
                                  value: undefined
                                }
                              ]);
                            }
                          }}
                        />
                      </Form.Item>
                      <Form.Item dependencies={[FacilityKey.RENTED_FROM]}>
                        {({getFieldValue}) => (
                          <Form.Item
                            name={FacilityKey.RENTED_TO}
                            // getValueProps={() => ({
                            //   value: data?.[FacilityKey.RENTED_TO]
                            //     ? moment(data?.[FacilityKey.RENTED_TO])
                            //     : undefined
                            // })}
                            label={FacilityLabel.RENTED_TO}
                            shouldUpdate={true}
                            rules={[
                              {
                                required: true,
                                message: CommonErrors.mandatory(
                                  FacilityLabel.RENTED_TO
                                )
                              }
                            ]}
                            validateFirst={true}
                            hasFeedback
                            labelCol={{span: 24}}
                            style={{marginBottom: '0px'}}
                            {...getDatePickerToMomentProps()}
                          >
                            <MomentDatePicker
                              disabledDate={(current: any) => {
                                return (
                                  getFieldValue(FacilityKey.RENTED_FROM) >
                                  current
                                );
                              }}
                              inputReadOnly
                              format="D MMM YYYY"
                              style={{width: '100%'}}
                              showNow={false}
                              disabled={!getFieldValue(FacilityKey.RENTED_FROM)}
                            />
                          </Form.Item>
                        )}
                      </Form.Item>
                    </>
                  )
                );
              }}
            </Form.Item>
          </Col>

          <Col className="gutter-row">
            <Form.Item
              name={FacilityKey.NO_EMP}
              rules={[
                // {
                //   required: true,
                //   message: CommonErrors.mandatory(FacilityLabel.NO_EMP)
                // },
                {
                  pattern: RegEx.noZeroNoNegative,
                  message: 'Please enter non decimal value more than 0'
                }
              ]}
              label={FacilityLabel.NO_EMP}
              labelCol={{span: 24}}
              initialValue={undefined}
              validateFirst
              hasFeedback
            >
              <NumberInput
                className="input"
                placeholder={`Enter ${FacilityLabel.NO_EMP}`}
              />
            </Form.Item>
          </Col>
          <Col className="gutter-row">
            <Form.Item
              name={FacilityKey.ADDRESS}
              rules={[
                {
                  required: true,
                  message: CommonErrors.mandatory(FacilityLabel.ADDRESS)
                },
                {
                  whitespace: true,
                  message: CommonErrors.mandatory(FacilityLabel.ADDRESS)
                }
              ]}
              label={FacilityLabel.ADDRESS}
              labelCol={{span: 24}}
              initialValue={''}
              normalize={(value) => transformInput.removeExtraSpace(value)}
              validateFirst
              hasFeedback
            >
              <Input
                className="input"
                placeholder={`Enter ${FacilityLabel.ADDRESS}`}
                onInput={(e) => {
                  const target = e.target as HTMLInputElement;
                  target.value = FirstCap(target.value);
                }}
              />
            </Form.Item>
          </Col>
          <Col className="gutter-row">
            <Form.Item
              name={FacilityKey.COUNTRY}
              label={FacilityLabel.COUNTRY}
              labelCol={{span: 24}}
              rules={[
                {
                  required: true,
                  message: CommonErrors.mandatory(FacilityLabel.COUNTRY)
                }
              ]}
              validateFirst
              hasFeedback
              // shouldUpdate
            >
              <Select
                showSearch
                className="select"
                placeholder={`Choose ${FacilityLabel.COUNTRY}`}
                filterOption={filterSelectOptions}
                options={countryOptions}
                onChange={(e) => {
                  if (
                    getFieldValue(FacilityKey.COUNTRY) &&
                    getFieldValue(FacilityKey.STATE)
                  ) {
                    setFields([
                      {
                        name: FacilityKey.STATE,
                        errors: ['Please Select State again'],
                        value: undefined
                      },
                      {
                        name: FacilityKey.CITY,
                        errors: ['Please Select City again'],
                        value: undefined
                      }
                    ]);
                  }
                  setSelectedCountry(e);
                }}
              />
            </Form.Item>
          </Col>
          <Col className="gutter-row">
            <Form.Item shouldUpdate noStyle>
              {({getFieldValue}) => (
                <Form.Item
                  name={FacilityKey.STATE}
                  label={FacilityLabel.STATE}
                  labelCol={{span: 24}}
                  // style={{marginBottom: '0px'}}
                  rules={[
                    {
                      required: true,
                      message: CommonErrors.mandatory(FacilityLabel.STATE)
                    }
                  ]}
                  validateFirst
                  hasFeedback
                >
                  <Select
                    showSearch
                    className="select"
                    placeholder={`Choose ${FacilityLabel.STATE}`}
                    disabled={!getFieldValue(FacilityKey.COUNTRY)}
                    filterOption={filterSelectOptions}
                    value={data?.[FacilityKey.STATE] ?? undefined}
                    onChange={() => {
                      if (getFieldValue(FacilityKey.CITY)) {
                        setFields([
                          {
                            name: FacilityKey.CITY,
                            errors: ['Please Select City again'],
                            value: undefined
                          }
                        ]);
                      }
                    }}
                    options={
                      getFieldValue(FacilityKey.COUNTRY)
                        ? stateOptions.filter(
                            (item) =>
                              item?.country_id ===
                              getFieldValue(FacilityKey.COUNTRY)
                          )
                        : [{value: 0, label: ''}]
                    }
                  />
                </Form.Item>
              )}
            </Form.Item>
          </Col>
          <Col className="gutter-row">
            <Form.Item shouldUpdate noStyle>
              {({getFieldValue}) => (
                <Form.Item
                  name={FacilityKey.CITY}
                  label={FacilityLabel.CITY}
                  labelCol={{span: 24}}
                  // style={{marginBottom: '0px'}}
                  rules={[
                    {
                      required: true,
                      message: CommonErrors.mandatory(FacilityLabel.CITY)
                    }
                  ]}
                  validateFirst
                  hasFeedback
                >
                  <Select
                    showSearch
                    className="select"
                    placeholder={`Choose ${FacilityLabel.CITY}`}
                    disabled={
                      !(
                        getFieldValue(FacilityKey.STATE) &&
                        getFieldValue(FacilityKey.COUNTRY)
                      )
                    }
                    value={
                      cityOptions?.length > 0
                        ? data?.[FacilityLabel.CITY]
                        : undefined
                    }
                    filterOption={filterSelectOptions}
                    options={
                      getFieldValue(FacilityKey.STATE)
                        ? cityOptions?.filter(
                            (item) =>
                              item?.state_id ===
                              getFieldValue(FacilityKey.STATE)
                          )
                        : [{value: 0, label: ''}]
                    }
                  />
                </Form.Item>
              )}
            </Form.Item>
          </Col>
          <Col className="gutter-row">
            <Form.Item
              name={FacilityKey.PINCODE}
              label={FacilityLabel.PINCODE}
              labelCol={{span: 24}}
              rules={[
                {
                  required: true,
                  message: CommonErrors.mandatory(FacilityLabel.PINCODE)
                },
                {
                  pattern: RegEx.pinCode,
                  message: 'Special characters are not allowed.'
                },
                {
                  whitespace: true,
                  message: CommonErrors.mandatory(FacilityLabel.PINCODE)
                }
              ]}
              validateFirst
              hasFeedback
            >
              <Input
                className="input"
                placeholder={`Enter ${FacilityLabel.PINCODE}`}
                onInput={(e) => {
                  const target = e.target as HTMLInputElement;
                  target.value = AllCap(target.value);
                }}
              />
            </Form.Item>
          </Col>
          <Col className="gutter-row">
            <Form.Item shouldUpdate noStyle>
              {({getFieldValue}) => (
                <Form.Item
                  name={FacilityKey.FLOOR_SPACE}
                  label={FacilityLabel.FLOOR_SPACE}
                  labelCol={{span: 24}}
                  initialValue={undefined}
                  rules={[
                    // {
                    //   required: true,
                    //   message: CommonErrors.mandatory(FacilityLabel.FLOOR_SPACE)
                    // },
                    {
                      pattern: RegEx.twoDigitDecimal,
                      message: CommonErrors.twoDigitPositive
                    }
                  ]}
                  validateFirst
                  hasFeedback
                  shouldUpdate
                >
                  <NumberInput
                    addonAfter={
                      FilteredUnit(getFieldValue(FacilityKey.COUNTRY)).area
                    }
                    className="input"
                    placeholder={`Enter ${FacilityLabel.FLOOR_SPACE}`}
                  />
                </Form.Item>
              )}
            </Form.Item>
          </Col>

          <Col className="gutter-row">
            <Form.Item dependencies={[FacilityKey.COUNTRY]} noStyle>
              {({getFieldValue}) => {
                return (
                  getFieldValue(FacilityKey.COUNTRY) === 101 && (
                    <Form.Item
                      name={FacilityKey.GST}
                      label={FacilityLabel.GST}
                      labelCol={{span: 24}}
                      rules={[
                        {
                          pattern: RegEx.GST,
                          message: CommonErrors.validField(FacilityLabel.GST)
                        }
                      ]}
                      initialValue={''}
                      validateFirst
                      hasFeedback
                    >
                      <Input
                        className="input"
                        onInput={(e) => {
                          const target = e.target as HTMLInputElement;
                          target.value = AllCap(target.value);
                        }}
                        placeholder={`Enter ${OPplaceholder.GST}`}
                      />
                    </Form.Item>
                  )
                );
              }}
            </Form.Item>
          </Col>
          {/* {companyDetails?.[OPKey.SUB_CATEGORY]?.includes(6) && (
            <>
              <Col className="gutter-row">
                <Form.Item
                  name={FacilityKey.rating}
                  label={FacilityLabel.rating}
                  labelCol={{span: 24}}
                  rules={[
                    {
                      required: true,
                      message: CommonErrors.mandatory(FacilityLabel.rating)
                    }
                  ]}
                  validateFirst
                  hasFeedback
                >
                  <Select
                    className="select"
                    placeholder={`Enter ${FacilityLabel.rating}`}
                    options={rating}
                  />
                </Form.Item>
              </Col>
              <Col className="gutter-row">
                <Form.Item
                  name={FacilityKey.occupancyRate}
                  label={FacilityLabel.occupancyRate}
                  rules={[
                    {
                      required: true,
                      message: CommonErrors.mandatory(
                        FacilityLabel.occupancyRate
                      )
                    },
                    {
                      pattern: RegEx.percentage,
                      message: CommonErrors.percentageError
                    }
                  ]}
                  labelCol={{span: 24}}
                  validateFirst
                  hasFeedback
                >
                  <NumberInput
                    placeholder={`Enter ${FacilityLabel.occupancyRate}`}
                    addonAfter="% / Year"
                  />
                </Form.Item>
              </Col>
              <Col className="gutter-row">
                <Form.Item
                  name={FacilityKey.totalNoOfRooms}
                  label={FacilityLabel.totalNoOfRooms}
                  rules={[
                    {
                      required: true,
                      message: CommonErrors.mandatory(
                        FacilityLabel.totalNoOfRooms
                      )
                    },
                    {
                      pattern: RegEx.noZeroNoNegative,
                      message: 'Please enter non decimal value more than 0'
                    }
                  ]}
                  labelCol={{span: 24}}
                  validateFirst
                  hasFeedback
                >
                  <NumberInput
                    placeholder={`Enter ${FacilityLabel.totalNoOfRooms}`}
                  />
                </Form.Item>
              </Col>
              <Col className="gutter-row">
                <Form.Item shouldUpdate>
                  {({getFieldValue}) => (
                    <Form.Item
                      name={FacilityKey.averageRoomSize}
                      label={FacilityLabel.averageRoomSize}
                      rules={[
                        {
                          required: true,
                          message: CommonErrors.mandatory(
                            FacilityLabel.averageRoomSize
                          )
                        },
                        {
                          pattern: RegEx.twoDigitDecimal,
                          message: CommonErrors.twoDigitPositive
                        }
                      ]}
                      labelCol={{span: 24}}
                      validateFirst
                      hasFeedback
                    >
                      <NumberInput
                        addonAfter={
                          FilteredUnit(getFieldValue(FacilityKey.COUNTRY)).area
                        }
                        placeholder={`Enter ${FacilityLabel.averageRoomSize}`}
                      />
                    </Form.Item>
                  )}
                </Form.Item>
              </Col>
            </>
          )}
          {hasSubCategory(companyDetails?.[OPKey.SUB_CATEGORY], 8) && (
            <>
              <Col className="gutter-row">
                <Form.Item
                  name={FacilityKey.resType}
                  label={FacilityLabel.resType}
                  labelCol={{span: 24}}
                  rules={[
                    {
                      required: true,
                      message: CommonErrors.mandatory(FacilityLabel.resType)
                    }
                  ]}
                  validateFirst
                  hasFeedback
                >
                  <Select
                    className="select"
                    placeholder={`Choose ${FacilityLabel.resType}`}
                    options={resType}
                  />
                </Form.Item>
              </Col>
              <Col className="gutter-row">
                <Form.Item
                  name={FacilityKey.averageMealsPerYear}
                  label={FacilityLabel.averageMealsPerYear}
                  rules={[
                    {
                      required: true,
                      message: CommonErrors.mandatory(
                        FacilityLabel.averageMealsPerYear
                      )
                    },
                    {
                      pattern: RegEx.noZeroNoNegative,
                      message: 'Please enter non decimal value more than 0'
                    }
                  ]}
                  labelCol={{span: 24}}
                  validateFirst
                  hasFeedback
                >
                  <NumberInput
                    placeholder={`Enter ${FacilityLabel.averageMealsPerYear}`}
                    addonAfter="per Year"
                  />
                </Form.Item>
              </Col>
            </>
          )} */}
        </>
      )}
      {currentStep === 1 && (
        <>
          <Col className="gutter-row">
            <Form.Item
              valuePropName="checked"
              name={FacilityKey.BIO_PROTECTED}
              label={FacilityLabel.BIO_PROTECTED}
              labelCol={{span: 24}}
              initialValue={false}
              tooltip={{
                icon: <InfoCircleOutlined />,
                title: faciInfo?.bioDiverse
              }}
              className="facilityFormSwitch"
              validateFirst
              hasFeedback
            >
              <Switch
                checkedChildren={'Yes'}
                unCheckedChildren={'No'}
                onChange={(e) => {
                  if (!e) {
                    setFields([
                      {
                        name: FacilityKey.SEVERE_IMPACT,
                        value: e
                      }
                    ]);
                  }
                }}
              />
            </Form.Item>
          </Col>
          <Col className="gutter-row">
            <Form.Item dependencies={[FacilityKey.BIO_PROTECTED]} noStyle>
              {({getFieldValue}) => {
                return (
                  getFieldValue(FacilityKey.BIO_PROTECTED) && (
                    <Form.Item
                      valuePropName="checked"
                      name={FacilityKey.SEVERE_IMPACT}
                      label={FacilityLabel.SEVERE_IMPACT}
                      labelCol={{span: 24}}
                      className="facilityFormSwitch"
                      validateFirst
                      hasFeedback
                    >
                      <Switch
                        checkedChildren={'Yes'}
                        unCheckedChildren={'No'}
                        defaultChecked={false}
                        onChange={() => {
                          setFields([
                            {
                              name: FacilityKey.IMPACTED_AREA,
                              value: undefined
                            }
                          ]);
                        }}
                      />
                    </Form.Item>
                  )
                );
              }}
            </Form.Item>
          </Col>
          <Col className="gutter-row">
            <Form.Item
              dependencies={[
                FacilityKey.BIO_PROTECTED,
                FacilityKey.SEVERE_IMPACT
              ]}
              noStyle
            >
              {({getFieldValue}) => {
                return (
                  getFieldValue(FacilityKey.SEVERE_IMPACT) && (
                    <Form.Item
                      name={FacilityKey.IMPACTED_AREA}
                      className="facilityFormSwitch"
                      rules={[
                        {
                          required: true,
                          message: CommonErrors.mandatory(
                            FacilityLabel.IMPACTED_AREA
                          )
                        },
                        {
                          pattern: RegEx.twoDigitDecimal,
                          message: CommonErrors.twoDigitPositive
                        }
                      ]}
                      initialValue={null}
                      label={FacilityLabel.IMPACTED_AREA}
                      labelCol={{span: 24}}
                      validateFirst
                      hasFeedback
                      shouldUpdate
                    >
                      <NumberInput
                        addonAfter={`${
                          FilteredUnit(getFieldValue(FacilityKey.COUNTRY)).area
                        }`}
                        className="input"
                        placeholder={`Enter ${FacilityLabel.IMPACTED_AREA}`}
                      />
                    </Form.Item>
                  )
                );
              }}
            </Form.Item>
          </Col>
          <Col className="gutter-row">
            <Form.Item
              valuePropName="checked"
              name={FacilityKey.BIO_ADJACENT_FACILITY}
              label={FacilityLabel.BIO_ADJACENT_FACILITY}
              labelCol={{span: 24}}
              initialValue={false}
              className="facilityFormSwitch"
              validateFirst
              hasFeedback
            >
              <Switch checkedChildren={'Yes'} unCheckedChildren={'No'} />
            </Form.Item>
          </Col>
          <Col className="gutter-row">
            <Form.Item
              valuePropName="checked"
              name={FacilityKey.WATER_SCARED}
              label={FacilityLabel.WATER_SCARED}
              labelCol={{span: 24}}
              initialValue={false}
              tooltip={{
                icon: <InfoCircleOutlined />,
                title: faciInfo?.waterScaredArea
              }}
              className="facilityFormSwitch"
              validateFirst
              hasFeedback
            >
              <Switch checkedChildren={'Yes'} unCheckedChildren={'No'} />
            </Form.Item>
          </Col>
          {/* ========== Add this facility to the past year/years? - With Error Messages BELOW ========== */}
          <Col className="gutter-row">
            <Form.Item
              valuePropName="checked"
              name={FacilityKey.ADD_TO_PAST_YEARS}
              label={FacilityLabel.ADD_TO_PAST_YEARS}
              labelCol={{span: 24}}
              initialValue={false}
              className="facilityFormSwitch"
            >
              <Switch
                checkedChildren="Yes"
                unCheckedChildren="No"
                disabled={getFieldValue(yearKeyLabel.key)?.includes('2000')}
                onChange={(checked) => {
                  if (!checked) {
                    FormInstance.setFieldsValue({
                      [FacilityKey.PAST_YEAR_FROM]: undefined,
                      [FacilityKey.PAST_YEAR_TO]: undefined
                    });
                    // Clear any previous errors
                    FormInstance.setFields([
                      {name: FacilityKey.PAST_YEAR_FROM, errors: []},
                      {name: FacilityKey.PAST_YEAR_TO, errors: []}
                    ]);
                  }
                  setAddToPastYears(checked);
                }}
              />
            </Form.Item>
          </Col>
          {/* <Col>
            <div>
              {getFieldValue(yearKeyLabel.key)?.includes('2000')
                ? `Past year is not allowed for below year ${getFieldValue(
                    yearKeyLabel.key
                  )}`
                : ''}
            </div>
          </Col> */}
          {/* Conditional Year Range - Error messages appear BELOW each Select */}
          <Col className="gutter-row">
            <Form.Item dependencies={[FacilityKey.ADD_TO_PAST_YEARS]} noStyle>
              {({getFieldValue}) => {
                const addToPastYearsValue = getFieldValue(
                  FacilityKey.ADD_TO_PAST_YEARS
                );
                if (!addToPastYearsValue) return null;

                const currentSelectedYear = getFieldValue(
                  yearKeyLabel.key
                )?.split('-')?.[0];

                const pastYearOptions = yearFilter.filter(
                  (year) => year?.value?.split('-')?.[0] < currentSelectedYear
                );

                if (pastYearOptions?.length === 0) return null;

                return (
                  <Row gutter={16}>
                    {/* From Year - with label + error below */}
                    {/* <div style={{flex: 1}}>
                        <div
                          style={{
                            marginBottom: 8,
                            fontWeight: 500,
                            fontSize: 14
                          }}
                        >
                          Start Year
                        </div> */}
                    <Col className="gutter-row" span={12}>
                      <Form.Item
                        name={FacilityKey.PAST_YEAR_FROM}
                        label={FacilityLabel.PAST_YEAR_FROM}
                        rules={[
                          {
                            required: true,
                            message: 'Please select start year'
                          }
                        ]}
                        style={{marginBottom: 16}}
                        validateTrigger={['onChange', 'onBlur']}
                      >
                        <Select
                          placeholder="Select year"
                          options={pastYearOptions}
                          suffixIcon={<YearCalender />}
                          onChange={() => {
                            setPastYearChange(!pastYearChange);
                          }}
                        />
                      </Form.Item>
                    </Col>
                    {/* </div> */}

                    {/* To Year - with label + error below */}
                    {/* <div style={{flex: 1}}>
                        <div
                          style={{
                            marginBottom: 8,
                            fontWeight: 500,
                            fontSize: 14
                          }}
                        ></div> */}
                    <Col className="gutter-row" span={12}>
                      <Form.Item
                        name={FacilityKey.PAST_YEAR_TO}
                        label={FacilityLabel.PAST_YEAR_TO}
                        dependencies={[FacilityKey.PAST_YEAR_FROM]}
                        rules={[
                          {required: true, message: 'Please select end year'},
                          ({getFieldValue}) => ({
                            // eslint-disable-next-line @typescript-eslint/promise-function-async
                            validator(_, value) {
                              const fromYear = getFieldValue(
                                FacilityKey.PAST_YEAR_FROM
                              );
                              if (!value || !fromYear) return Promise.resolve();
                              if (fromYear > value) {
                                return Promise.reject(
                                  new Error(
                                    'End year must be greater than or equal to start year'
                                  )
                                );
                              }

                              return Promise.resolve();
                            }
                          })
                        ]}
                        style={{marginBottom: 16}}
                        validateTrigger={['onBlur']}
                      >
                        <Select
                          placeholder="Select year"
                          onChange={() => {
                            setPastYearChange(!pastYearChange);
                          }}
                          options={pastYearOptions}
                          suffixIcon={<YearCalender />}
                          disabled={!getFieldValue(FacilityKey.PAST_YEAR_FROM)}
                        />
                      </Form.Item>
                    </Col>

                    {/* </div> */}
                  </Row>
                );
              }}
            </Form.Item>
          </Col>
          <Col className="gutter-row">
            <div>{duplicateFacilityMessage}</div>
          </Col>
        </>
      )}
    </>
  );
};

export default FacilityForm;
